import debounce from 'lodash/debounce';
import type { PropsWithChildren } from 'react';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { SDrag, SPanel, SRoot } from './SplitPanel.styles';

const RESIZER_DRAG_WIDTH = 3;

enum DragStatus {
  IDLE = 'idle',
  DRAG_START = 'drag-start',
  DRAGGING = 'dragging',
  DRAG_END = 'drag-end',
}

type Sizes = {
  width: number[];
  height: number[];
};

export type DragPos = { x: number[]; y: number[] };

const initialSizes: Sizes = {
  width: [],
  height: [],
};

const convertX = (pageX: number, elX: number) => pageX - elX;

type SplitPanelProps = PropsWithChildren<{
  isVertical?: boolean;
  performanceMode?: boolean;
  dragPos?: DragPos;
  onDragEnd?: (drag: DragPos) => void;
}>;

export function SplitPanel({
  children,
  dragPos,
  onDragEnd,
  isVertical = false,
  performanceMode = false,
}: SplitPanelProps) {
  const [status, setStatus] = useState<DragStatus>(DragStatus.IDLE);
  const [drag, setDrag] = useState<DragPos>({ x: [], y: [] });
  const [rootEl, setRootEl] = useState<{
    width: number;
    height: number;
    left: number;
    top: number;
    el: HTMLDivElement | null;
  }>({ width: 0, height: 0, el: null, left: 0, top: 0 });

  const [childrenSize, setChildrenSize] = useState<Sizes>(initialSizes);

  const rootElRef = useRef<HTMLDivElement>(null);
  const activeDragRef = useRef<number>(0);

  const _children = React.Children.toArray(children).filter(Boolean);
  const childrenCount = _children.length;
  const dragElsCount = childrenCount - 1;

  const updateChildrenWidth = () => {
    const width: number[] = [];
    React.Children.forEach(_children, (_, i) => {
      const _dragX = drag.x[i];
      const _prevDragX = drag.x[i - 1] || 0;
      width[i] = _dragX ? _dragX - _prevDragX : rootEl.width - _prevDragX;
    });

    setChildrenSize({ ...childrenSize, width });
  };

  const updateChildrenHeight = () => {
    const height: number[] = [];
    React.Children.forEach(_children, (_, i) => {
      const _dragY = drag.y[i];
      const _prevDragY = drag.y[i - 1] || 0;
      height[i] = _dragY ? _dragY - _prevDragY : rootEl.height - _prevDragY;
    });

    setChildrenSize({ ...childrenSize, height });
  };

  useEffect(() => {
    const onResize = () => {
      if (!rootElRef.current) return;

      const rootRect = rootElRef.current.getBoundingClientRect();
      setRootEl({
        el: rootElRef.current,
        left: rootRect.left,
        top: rootRect.top,
        width: rootRect.width,
        height: rootRect.height,
      });
    };

    onResize();
    const _onResize = debounce(onResize, 500);
    window.addEventListener('resize', _onResize, false);

    return () => window.removeEventListener('resize', _onResize, false);
  }, []);

  useEffect(() => {
    const shiftX = dragElsCount === 0 ? 0 : rootEl.width / childrenCount;
    const shiftY = dragElsCount === 0 ? 0 : rootEl.height / childrenCount;

    let i = 0;
    const newDragX = [];
    const newDragY = [];
    while (i < dragElsCount) {
      i++;

      const _shiftX = dragPos?.x[i - 1] ?? shiftX * i;
      const _shiftY = dragPos?.y[i - 1] ?? shiftY * i;

      newDragX.push(_shiftX);
      newDragY.push(_shiftY);
    }

    setDrag({ y: newDragY, x: newDragX });
  }, [rootEl, childrenCount]);

  useEffect(() => {
    if (performanceMode ? status !== DragStatus.DRAG_END : false) {
      return;
    }

    if (isVertical) {
      updateChildrenHeight();
    } else {
      updateChildrenWidth();
    }
  }, [drag, status, isVertical]);

  useEffect(() => {
    if (status === DragStatus.DRAG_END) {
      onDragEnd?.(drag);
    }
  }, [status]);

  const onMouseMove = (e: MouseEvent) => {
    setStatus(DragStatus.DRAGGING);

    if (isVertical) {
      const _dragY = convertX(e.pageY, rootEl.top);
      const _prevDragY = drag.y[activeDragRef.current - 1] || 0;
      const _nextDragY = drag.y[activeDragRef.current + 1] || rootEl.width;

      if (_dragY > _prevDragY && _dragY < _nextDragY) {
        drag.y[activeDragRef.current] = _dragY;
        setDrag({ ...drag });
      }
    } else {
      const _dragX = convertX(e.pageX, rootEl.left);
      const _prevDragX = drag.x[activeDragRef.current - 1] || 0;
      const _nextDragX = drag.x[activeDragRef.current + 1] || rootEl.width;

      if (_dragX > _prevDragX && _dragX < _nextDragX) {
        drag.x[activeDragRef.current] = _dragX;
        setDrag({ ...drag });
      }
    }
  };

  const onMouseUp = () => {
    setStatus(DragStatus.DRAG_END);
    document.body.style.userSelect = '';

    document.removeEventListener('mousemove', onMouseMove, false);
    document.removeEventListener('mouseup', onMouseUp, false);
  };

  const onMouseDown = (e: React.MouseEvent, i: number) => {
    setStatus(DragStatus.DRAG_START);
    document.body.style.userSelect = 'none';

    e.persist();
    activeDragRef.current = i;

    document.addEventListener('mousemove', onMouseMove, false);
    document.addEventListener('mouseup', onMouseUp, false);
  };

  const renderChildren = () => {
    return React.Children.map(_children, (child, i) => {
      return (
        <Fragment key={i}>
          <SPanel
            $isVertical={isVertical}
            $height={childrenSize.height[i]}
            $width={childrenSize.width[i]}
            $padding={RESIZER_DRAG_WIDTH / 2}
          >
            {child}
          </SPanel>

          {i < dragElsCount ? (
            <SDrag
              role="button"
              aria-label="Drag"
              tabIndex={-1}
              $isVertical={isVertical}
              $size={RESIZER_DRAG_WIDTH}
              onMouseDown={e => onMouseDown(e, i)}
            />
          ) : null}
        </Fragment>
      );
    });
  };

  return (
    <SRoot ref={rootElRef} $isVertical={isVertical}>
      {renderChildren()}
    </SRoot>
  );
}
